import { ELEMENT_IDS } from '../config';

export class PWAManager {
  private deferredPrompt: any = null;

  constructor() {
    // Only setup if not already installed
    if (!this.isPWAInstalled()) {
      this.setupInstallPrompt();
    }
  }

  isPWAInstalled(): boolean {
    // Check multiple indicators for PWA installation
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    const isInApp = window.matchMedia("(display-mode: fullscreen)").matches ||
                   window.matchMedia("(display-mode: minimal-ui)").matches;
    
    return isStandalone || isIOSStandalone || isInApp;
  }

  private getPlatform(): 'ios' | 'android' | 'desktop' | 'other' {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/ipad|iphone|ipod/.test(userAgent)) {
      return 'ios';
    } else if (/android/.test(userAgent)) {
      return 'android';
    } else if (/win|mac|linux/.test(userAgent) && !/mobile/.test(userAgent)) {
      return 'desktop';
    } else {
      return 'other';
    }
  }

  private canShowNativePrompt(): boolean {
    return this.deferredPrompt !== null;
  }

  private setupInstallPrompt(): void {
    // Listen for the beforeinstallprompt event (Chrome/Edge on Android/Desktop)
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallScreen();
    });

    // For platforms that don't support beforeinstallprompt
    // Show manual instructions after a short delay
    setTimeout(() => {
      if (!this.deferredPrompt) {
        const platform = this.getPlatform();
        if (platform === 'ios') {
          this.showInstallScreen();
        } else if (platform === 'android') {
          // Check if we can still trigger install on Android
          this.checkAndroidInstallability();
        }
      }
    }, 1000);
  }

  private async checkAndroidInstallability(): Promise<void> {
    // Try to detect if PWA installation is possible on Android
    // even without beforeinstallprompt
    const platform = this.getPlatform();
    if (platform === 'android') {
      // Check for Chrome or Chrome-based browsers on Android
      const isChrome = /chrome|chromium|edg/i.test(navigator.userAgent);
      const isInAppBrowser = /wv|webview/i.test(navigator.userAgent);
      
      if (isChrome && !isInAppBrowser) {
        // Likely supports PWA installation
        this.showInstallScreen();
      } else {
        // Show manual instructions for other browsers
        this.showInstallScreen();
      }
    }
  }

  setupEventListeners(
    onInstall: () => void,
    onSkip: () => void
  ): void {
    // Update UI content based on platform before setting up listeners
    this.updateInstallContent();

    document.getElementById(ELEMENT_IDS.INSTALL_BTN)?.addEventListener("click", async () => {
      await this.installPWA();
      onInstall(); // Always call the callback after install attempt
    });

    document.getElementById(ELEMENT_IDS.SKIP_INSTALL)?.addEventListener("click", onSkip);
  }

  private updateInstallContent(): void {
    const platform = this.getPlatform();
    const titleEl = document.querySelector('#install-screen h1');
    const descEl = document.querySelector('#install-screen p');
    const installBtn = document.getElementById(ELEMENT_IDS.INSTALL_BTN);

    if (titleEl && descEl && installBtn) {
      // Reset button display first
      installBtn.style.display = 'block';
      
      if (platform === 'ios') {
        // iOS Safari manual instructions - hide install button, only show skip
        titleEl.textContent = 'Add to Home Screen';
        descEl.innerHTML = 'Tap the share button <img src="/share-apple.svg" style="width: 20px; height: 20px; vertical-align: middle; margin: 0 4px; filter: brightness(0) invert(1);"> at the bottom, then select "Add to Home Screen"';
        installBtn.style.display = 'none'; // Hide the install button
      } else if (this.canShowNativePrompt() || platform === 'desktop') {
        // Native install prompt available OR desktop (Chrome/Edge/Firefox)
        titleEl.textContent = 'Install 360 Hair Cam';
        descEl.textContent = 'Install this app for the best experience';
        installBtn.textContent = 'Install App';
      } else if (platform === 'android') {
        // Android browsers
        const isChrome = /chrome|chromium|edg/i.test(navigator.userAgent.toLowerCase());
        if (isChrome) {
          titleEl.textContent = 'Install 360 Hair Cam';
          descEl.textContent = 'Install this app for the best experience on your device';
          installBtn.textContent = 'Install';
        } else {
          titleEl.textContent = 'Add to Home Screen';
          descEl.textContent = 'Open browser menu (⋮) and select "Add to Home Screen" or "Install App"';
          installBtn.style.display = 'none'; // Hide pointless button, only show Skip
        }
      } else {
        // Fallback
        titleEl.textContent = 'Use 360 Hair Cam';
        descEl.textContent = 'For the best experience, add this app to your home screen';
        installBtn.style.display = 'none'; // Hide pointless button, only show Skip
      }
    }
  }

  private async installPWA(): Promise<void> {
    if (this.deferredPrompt) {
      // Native install prompt available
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      if (outcome === "accepted") {
        this.deferredPrompt = null;
      }
    } else {
      // Try alternative installation methods
      const platform = this.getPlatform();
      
      if (platform === 'android') {
        const isChrome = /chrome|chromium|edg/i.test(navigator.userAgent.toLowerCase());
        if (isChrome) {
          // Try to trigger Chrome's native installation flow
          // Check if we missed the beforeinstallprompt event
          await this.tryAndroidInstall();
        }
      }
      
      // For all other cases, user has seen manual instructions, just continue
    }
  }

  private async tryAndroidInstall(): Promise<boolean> {
    // On Android Chrome, sometimes the beforeinstallprompt event fires later
    // or there might be other ways to trigger installation
    
    // First, try waiting a bit more for the deferred prompt
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      if (outcome === "accepted") {
        this.deferredPrompt = null;
      }
      return true; // Installation attempt was made
    }

    // If still no deferred prompt, guide user to manual installation
    // Update the content to show manual instructions
    const descEl = document.querySelector('#install-screen p');
    const installBtn = document.getElementById(ELEMENT_IDS.INSTALL_BTN);
    
    if (descEl && installBtn) {
      descEl.textContent = 'Look for "Add to Home Screen" in your browser menu (⋮) or install icon in the address bar';
      installBtn.style.display = 'none'; // Hide button, only show Skip
    }
    
    return false; // No installation prompt available
  }

  private showInstallScreen(): void {
    this.showScreen(ELEMENT_IDS.INSTALL_SCREEN);
  }

  private showScreen(screenId: string): void {
    document.querySelectorAll(".screen").forEach((screen) => {
      screen.classList.add("hidden");
    });
    document.getElementById(screenId)?.classList.remove("hidden");
  }
}